import { useEffect, useState } from "react";
import {
  KeyRound,
  Plus,
  X,
  Copy,
  Check,
  Ban,
  ShieldAlert,
  History,
  RefreshCw,
} from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmt(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

export default function ApiKeys() {
  const { user, hasPermission } = useAuth();
  const canManage = user?.role === "admin" || hasPermission("manage_api_keys");

  const [keys, setKeys] = useState([]);
  const [scopes, setScopes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(null); // { name, description, scopes: [] }
  const [newSecret, setNewSecret] = useState(null); // one-time reveal
  const [copied, setCopied] = useState(false);
  const [auditFor, setAuditFor] = useState(null); // { key, events }

  async function load() {
    setBusy(true);
    try {
      const [k, s] = await Promise.all([
        api.get("/admin/api-keys"),
        api.get("/admin/api-keys/scopes"),
      ]);
      setKeys(k.data.keys || []);
      setScopes(s.data.scopes || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function startCreate() {
    setError("");
    setNewSecret(null);
    setCreating({ name: "", description: "", scopes: [] });
  }

  function toggleScope(s) {
    setCreating((c) => ({
      ...c,
      scopes: c.scopes.includes(s) ? c.scopes.filter((x) => x !== s) : [...c.scopes, s],
    }));
  }

  async function submitCreate() {
    setError("");
    if (!creating.name.trim()) return setError("Name is required");
    if (!creating.scopes.length) return setError("Select at least one permission scope");
    setBusy(true);
    try {
      const r = await api.post("/admin/api-keys", {
        name: creating.name.trim(),
        description: creating.description.trim(),
        scopes: creating.scopes,
      });
      setCreating(null);
      setNewSecret(r.data); // contains api_key — shown exactly once
      setCopied(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(k) {
    if (
      !window.confirm(
        `Revoke API key "${k.name}" (${k.prefix}…)?\n\n` +
          "Any external application using it will immediately start receiving 401 " +
          "responses. This cannot be undone — issue a new key instead."
      )
    )
      return;
    setBusy(true);
    try {
      await api.post(`/admin/api-keys/${k.key_id}/revoke`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function openAudit(k) {
    setBusy(true);
    try {
      const r = await api.get(`/admin/api-keys/${k.key_id}/audit`, { params: { limit: 100 } });
      setAuditFor({ key: k, events: r.data.events || [] });
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }

  async function copySecret() {
    try {
      await navigator.clipboard.writeText(newSecret.api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard blocked (non-HTTPS / permissions) — the field is selectable as a fallback.
      alert("Copy failed — select the key text and copy it manually.");
    }
  }

  if (!canManage) {
    return (
      <div className="p-6 lg:p-8 max-w-3xl mx-auto" data-testid="api-keys-page">
        <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          <ShieldAlert size={16} /> You do not have permission to manage API keys.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="api-keys-page">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <KeyRound size={22} className="text-orange-600" />
          <h1 className="text-2xl font-bold text-slate-900">API Keys</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50 disabled:opacity-50"
            data-testid="api-keys-refresh"
          >
            <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-md font-semibold"
            data-testid="api-key-new-btn"
          >
            <Plus size={14} /> New API Key
          </button>
        </div>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Keys let external systems read NPD data over{" "}
        <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">
          {API_BASE}/external/npd/…
        </code>{" "}
        using the <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">X-API-Key</code>{" "}
        header. Keys are read-only and cannot sign in to this application.
      </p>

      {error && !creating && (
        <div className="mb-4 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded" data-testid="api-keys-error">
          {error}
        </div>
      )}

      {/* ---------- One-time secret reveal ---------- */}
      {newSecret && (
        <div
          className="mb-5 border-2 border-amber-400 bg-amber-50 rounded-lg p-4"
          data-testid="api-key-secret-panel"
        >
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <div className="font-bold text-amber-900 text-sm">
                Copy this key now. It will not be shown again.
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                This is the only time the full secret for “{newSecret.name}” is displayed. Store it
                in the external application’s secret configuration — never in a browser, a
                spreadsheet or source control.
              </div>
            </div>
            <button
              onClick={() => setNewSecret(null)}
              className="text-amber-700 hover:text-amber-900 flex-shrink-0"
              data-testid="api-key-secret-dismiss"
              title="I've copied it — hide"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={newSecret.api_key}
              onFocus={(e) => e.target.select()}
              className="flex-1 font-mono text-xs bg-white border border-amber-300 rounded-md px-3 py-2 text-slate-900"
              data-testid="api-key-secret-value"
            />
            <button
              onClick={copySecret}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-md font-semibold"
              data-testid="api-key-secret-copy"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="mt-3 text-[11px] text-amber-900">
            <div className="font-semibold mb-1">Test it from the external application:</div>
            <pre className="bg-white border border-amber-200 rounded p-2 overflow-x-auto font-mono text-[11px] text-slate-700">
{`curl -H "X-API-Key: ${newSecret.api_key.slice(0, 12)}…" \\
  "${API_BASE}/external/npd/whoami"`}
            </pre>
          </div>
        </div>
      )}

      {/* ---------- Create form ---------- */}
      {creating && (
        <div className="mb-5 bg-white border border-slate-200 rounded-lg p-4" data-testid="api-key-create-form">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-800">New API Key</h2>
            <button onClick={() => setCreating(null)} className="text-slate-400 hover:text-slate-700">
              <X size={16} />
            </button>
          </div>
          {error && (
            <div className="mb-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded" data-testid="api-key-create-error">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Name *</label>
              <input
                value={creating.name}
                onChange={(e) => setCreating({ ...creating, name: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm"
                placeholder="e.g. MRM Integration"
                data-testid="api-key-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Description</label>
              <input
                value={creating.description}
                onChange={(e) => setCreating({ ...creating, description: e.target.value })}
                className="w-full border border-slate-200 rounded-md px-3 py-1.5 text-sm"
                placeholder="What will use this key?"
                data-testid="api-key-desc-input"
              />
            </div>
          </div>
          <div className="mb-4">
            <label className="block text-xs text-slate-500 mb-2">
              Permissions (scopes) * — grant only what the external system needs
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {scopes.map((s) => (
                <label
                  key={s.scope}
                  className="flex items-start gap-2 border border-slate-200 rounded-md px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer"
                  data-testid={`api-key-scope-${s.scope}`}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={creating.scopes.includes(s.scope)}
                    onChange={() => toggleScope(s.scope)}
                  />
                  <span>
                    <span className="font-mono text-xs text-slate-900">{s.scope}</span>
                    <span className="text-slate-500"> — {s.label}</span>
                    <div className="text-[11px] text-slate-500">{s.description}</div>
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={submitCreate}
              disabled={busy}
              className="px-4 py-1.5 text-sm bg-orange-600 hover:bg-orange-700 text-white rounded-md font-semibold disabled:opacity-50"
              data-testid="api-key-create-submit"
            >
              Generate Key
            </button>
            <button
              onClick={() => setCreating(null)}
              className="px-4 py-1.5 text-sm border border-slate-200 rounded-md text-slate-600 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ---------- Key list ---------- */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm" data-testid="api-keys-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Name</th>
                <th className="text-left px-4 py-2 font-medium">Key</th>
                <th className="text-left px-4 py-2 font-medium">Scopes</th>
                <th className="text-left px-4 py-2 font-medium">Created</th>
                <th className="text-left px-4 py-2 font-medium">Last used</th>
                <th className="text-right px-4 py-2 font-medium">Requests</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keys.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-sm text-slate-400">
                    No API keys yet. Create one to let an external system read NPD data.
                  </td>
                </tr>
              )}
              {keys.map((k) => (
                <tr key={k.key_id} className="hover:bg-slate-50" data-testid={`api-key-row-${k.key_id}`}>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">{k.name}</div>
                    {k.description && <div className="text-xs text-slate-500">{k.description}</div>}
                    <div className="text-[10px] text-slate-400 font-mono">{k.key_id}</div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">{k.prefix}…</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(k.scopes || []).map((s) => (
                        <span
                          key={s}
                          className="inline-block px-1.5 py-0.5 text-[10px] font-mono bg-sky-100 text-sky-800 rounded"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">
                    {fmt(k.created_at)}
                    <div className="text-[10px] text-slate-400">{k.created_by_name || ""}</div>
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-600">{fmt(k.last_used_at)}</td>
                  <td className="px-4 py-2 text-xs text-slate-600 text-right">{k.request_count ?? 0}</td>
                  <td className="px-4 py-2">
                    {k.active ? (
                      <span className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-emerald-100 text-emerald-800 rounded">
                        ACTIVE
                      </span>
                    ) : (
                      <span
                        className="inline-block px-2 py-0.5 text-[10px] font-semibold bg-slate-200 text-slate-600 rounded"
                        title={`Revoked ${fmt(k.revoked_at)}${k.revoked_by_name ? ` by ${k.revoked_by_name}` : ""}`}
                      >
                        REVOKED
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => openAudit(k)}
                      className="text-slate-400 hover:text-sky-600 p-1 mr-1"
                      title="View audit log"
                      data-testid={`api-key-audit-${k.key_id}`}
                    >
                      <History size={14} />
                    </button>
                    {k.active && (
                      <button
                        onClick={() => revoke(k)}
                        className="text-slate-400 hover:text-red-600 p-1"
                        title="Revoke key"
                        data-testid={`api-key-revoke-${k.key_id}`}
                      >
                        <Ban size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ---------- Integration reference ---------- */}
      <div className="mt-6 bg-white border border-slate-200 rounded-lg p-4" data-testid="api-keys-docs">
        <h2 className="font-semibold text-slate-800 mb-2 text-sm">How an external system uses a key</h2>
        <pre className="bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto font-mono text-[11px] leading-relaxed">
{`# Verify the key and see its scopes
curl -H "X-API-Key: npd_xxxxxxxx" "${API_BASE}/external/npd/whoami"

# Read NPD costings (scope: npd.read), paginated
curl -H "X-API-Key: npd_xxxxxxxx" \\
  "${API_BASE}/external/npd/costings?limit=50&page=1"

# Incremental sync — only records changed since a timestamp
curl -H "X-API-Key: npd_xxxxxxxx" \\
  "${API_BASE}/external/npd/costings?updated_since=2026-01-01T00:00:00Z"`}
        </pre>
        <div className="mt-3 text-xs text-slate-600 space-y-1">
          <div>
            <span className="font-semibold">401</span> — <code className="font-mono">Missing X-API-Key header</code>{" "}
            / <code className="font-mono">Invalid API key</code> /{" "}
            <code className="font-mono">API key has been revoked</code>
          </div>
          <div>
            <span className="font-semibold">403</span> —{" "}
            <code className="font-mono">API key does not have permission for this resource</code>
          </div>
          <div>
            Full endpoint reference:{" "}
            <a
              href={`${API_BASE.replace(/\/api$/, "")}/docs#/external%20api`}
              target="_blank"
              rel="noreferrer"
              className="text-sky-600 hover:underline"
            >
              OpenAPI documentation
            </a>
          </div>
        </div>
      </div>

      {/* ---------- Audit modal ---------- */}
      {auditFor && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setAuditFor(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-testid="api-key-audit-modal"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <div>
                <div className="font-semibold text-slate-800 text-sm">Audit log — {auditFor.key.name}</div>
                <div className="text-xs text-slate-500 font-mono">{auditFor.key.prefix}…</div>
              </div>
              <button onClick={() => setAuditFor(null)} className="text-slate-400 hover:text-slate-700">
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">When</th>
                    <th className="text-left px-4 py-2 font-medium">Event</th>
                    <th className="text-left px-4 py-2 font-medium">Endpoint</th>
                    <th className="text-left px-4 py-2 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {auditFor.events.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-slate-400">
                        No activity recorded.
                      </td>
                    </tr>
                  )}
                  {auditFor.events.map((e) => (
                    <tr key={e.audit_id}>
                      <td className="px-4 py-1.5 text-slate-600 whitespace-nowrap">{fmt(e.timestamp)}</td>
                      <td className="px-4 py-1.5 font-mono text-slate-700">{e.event}</td>
                      <td className="px-4 py-1.5 font-mono text-slate-500">
                        {e.method ? `${e.method} ${e.path}` : e.detail || "—"}
                      </td>
                      <td className="px-4 py-1.5">
                        <span
                          className={`font-semibold ${e.success ? "text-emerald-700" : "text-red-600"}`}
                        >
                          {e.status_code || (e.success ? "ok" : "—")}
                        </span>
                        {!e.success && e.detail && (
                          <span className="text-slate-500"> — {e.detail}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
